import { Button, Dialog, DialogContent, DialogTitle, Drawer, IconButton, Menu, MenuItem } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "./flow.scss";
import { toast } from "react-toastify";
import {
  BUTTON_HEIGHT,
  BUTTON_PADDING_TOP,
  BUTTON_ROW_GAP,
  ButtonData,
  drawButton,
  drawCircle,
  drawConditionBranch,
  drawCubicArrow,
  edgeExists,
  EdgeLayout,
  getInPoint,
  getMousePositions,
  getOutPoint,
  hitTest,
  hitTestEdges,
  inRect,
  measureTextHeight,
  NODE_MENU_OFFSET_Y,
  NODE_PADDING_BOTTOM,
  NODE_PADDING_LEFT,
  NODE_PADDING_TOP,
  NodeLayout,
  NodeType,
  OUTPUT_HEIGHT,
  resizeCanvas,
  screenToWorld,
  setupCanvas,
  wrapText,
} from "./canvas-utils";
import { Node, Edge } from "./canvas-utils";
import withRouter from "hoc/withRouter";
import { automationsService } from "services/admin/automationsService";
import { withRemoteDataAndSpinner } from "hoc/withRemoteDataAndSpinner";
import { useLoadAll } from "hooks/useLoadAll";
import { FaArrowLeft, FaMinus, FaSave, FaTimes } from "react-icons/fa";
import { FormProvider, useForm } from "react-hook-form";
import triggerTypesAdminService from "services/admin/triggerTypesService";
import AutomationTriggerV2 from "./AutomationTriggerV2";
import NodeTextForm from "./NodeTextForm";
import _ from "lodash";
import ButtonEditForm from "./ButtonEditForm";
import ActionEditForm, { actionTypesMap } from "./ActionEditForm";
import DelayEditForm from './DelayEditForm'
import { ConditionEditForm } from "./ConditionEditForm";
import { FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { appContext } from "context";
import { NodeStatsDialog } from "./NodeStatsDialog";
import { ButtonStatsDialog } from "./ButtonStatsDialog";


export const MOVE_THRESHOLD = 5;
export interface ActiveTarget {
  type: "node" | "edge" | "condition";
  entityID: string;
  entity: Node | Edge;
  payload?: any;
}

/**
 * hit-test - проверка попадания
 * Как в ManyChat: drag для создания стрелки (логика)
 * 🔹 mousedown на выходе ноды
 * 🔹 mousemove → временная линия
 * 🔹 mouseup над другой нодой → сохранить edge
 */

/**
 * Canvas — это «перерисовать», а не «двинуть»
 * @returns
 * Pan
 * Ты двигаешь камеру, а не мир
 * → движение в пикселях экрана
 * --------------------------------
 * Drag node
 * Ты двигаешь объект в мире
 * → движение в world-координатах
 */
const AutomationConstructor = ({
  router: {
    params: { automationID },
  },
  data: { automation: { automation, nodeStats }, triggerTypes },
}: any) => {
  const methods = useForm({
    defaultValues: {
      automationTriggers: automation?.automationTriggers || [
        {
          triggerTypeID: "",
          settings: {},
        },
      ],
    },
    mode: "onChange",
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawRef = useRef(false);

  /**
   * Состояние канваса
   * scale - масштаб канваса
   * offsetX - смещение канваса по X
   * offsetY - смещение канваса по Y
   */
  const canvasStateRef = useRef(automation?.canvasState || {
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    scaleStep: 1.5,
  });

  // Массив элементов на канвасе
  const nodesRef = useRef<Node[]>(automation?.nodes || []);

  // Массив связей между узлами
  const edgesRef = useRef<Edge[]>(automation?.edges || []);

  // Активный элемент
  const [activeTarget, setActiveTarget] = useState<ActiveTarget | null>(null);
  const activeNodeRef = useRef<Node | null>(null);
  // Флаг перетаскивания
  const isDraggingRef = useRef(false);
  // Смещение элемента
  const offsetRef = useRef({ x: 0, y: 0 });

  // хранит откуда началась стрелка
  const linkingFromRef = useRef<{
    node: Node;
    buttonID?: string; // если undefined → обычный output
    branchID?: string; // если undefined → обычный output
  } | null>(null);

  // текущий конец стрелки
  const tempPointRef = useRef<{ x: number; y: number } | null>(null);
  // Храним выбранную стрелку
  const selectedEdgeRef = useRef<Edge | null>(null);

  // стартовую позицию. Нужно считать движением только если курсор сдвинулся больше чем на N пикселей, например 3–5px.
  const dragStartRef = useRef<{ x: number, y: number } | null>(null);

  // Icons
  const instagramLogoRef = useRef<HTMLImageElement>(new Image());
  const messageIconRef = useRef<HTMLImageElement>(new Image());
  const deleteNodeIconRef = useRef<HTMLImageElement>(new Image());
  const actionIconRef = useRef<HTMLImageElement>(new Image());
  const delayIconRef = useRef<HTMLImageElement>(new Image());
  const conditionIconRef = useRef<HTMLImageElement>(new Image());

  // Add a ref to track current world mouse for the red dot
  const mouseRef = useRef({ x: 0, y: 0 });

  // был ли реальный drag между down и up.
  const didMoveRef = useRef(false);

  // Диалоги
  const [editTextNodeOpen, setEditTextNodeOpen] = useState(false);

  // Sidebars
  const [triggerSidebarOpen, setTriggerSidebarOpen] = useState(false);
  const [buttonSidebarOpen, setButtonSidebarOpen] = useState(false);
  const [actionSidebarOpen, setActionSidebarOpen] = useState(false);
  const [delaySidebarOpen, setDelaySidebarOpen] = useState(false);
  const [branchSidebarOpen, setBranchSidebarOpen] = useState(false);
  const [statsSidebarOpen, setStatsSidebarOpen] = useState(false);

  // Menu
  const [menuOpen, setMenuOpen] = useState<Element | null>(null);

  const sidebarOpen = useSelector((state: any) => state.app.sidebar.open);

  const navigate = useNavigate();

  useEffect(() => {
    // Иконки && Disable scroll on body
    document.body.style.overflow = "hidden";
    (document.querySelector("#admin-header") as any).style.display = "none";
    (document.querySelector('.admin-content') as any).style.paddingLeft = "0";

    instagramLogoRef.current.src = "/media/images/instagram.png";

    messageIconRef.current = new Image();
    messageIconRef.current.src = "/media/icons/message.svg";

    deleteNodeIconRef.current = new Image();
    deleteNodeIconRef.current.src = "/media/icons/trash.svg";

    actionIconRef.current = new Image();
    actionIconRef.current.src = "/media/icons/flash.svg";

    delayIconRef.current = new Image();
    delayIconRef.current.src = "/media/icons/delay.svg";

    conditionIconRef.current = new Image();
    conditionIconRef.current.src = "/media/icons/condition.svg";
    return () => {
      document.body.style.overflow = "auto";
      (document.querySelector("#admin-header") as any).style.display = "flex";
      (document.querySelector('.admin-content') as any).style.paddingLeft = "24px";
    };
  }, []);

  useEffect(() => {
    // Настраиваем канвас
    const canvas = canvasRef.current;

    if (!canvas) return;

    setupCanvas(canvas);
    requestDraw();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    resizeCanvas(canvas);
    requestDraw();
  }, [sidebarOpen]);

  useEffect(() => {
    // Обработчики событий мыши
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Флаг перетаскивания канваса
    let isPanning = false;
    let startX = 0;
    let startY = 0;

    const onMouseDown = (e: MouseEvent) => {
      /**
       * Когда кликаем на канвасе:
       * 1. Проверяет, попал ли клик в какую-то ноду (Node). Если попали в ноду → включаем режим перетаскивания
       * 2. Если НЕ попали в ноду → включаем панорамирование. Теперь при движении мыши будет двигаться «камера», а не нода.
       */

      const { mouse, screenX, screenY } = getMousePositions(e, canvas, canvasStateRef);

      dragStartRef.current = { x: screenX, y: screenY };
      didMoveRef.current = false;

      // 2. ПРОВЕРКА НАЖАТИЯ НА НОДУ (используем mouse.x и mouse.y)
      for (let i = nodesRef.current.length - 1; i >= 0; i--) {
        const node = nodesRef.current[i];

        const hit = hitTest(node, mouse.x, mouse.y);
        if (!hit) continue;
        console.log("MouseDown: Клик по ", hit.type);

        // Кнопка выхода
        if (hit.type === "button-out-point") {
          // toast.success(`MouseDown: Клик по ${hit.type} ${hit.button?.id}`);
          linkingFromRef.current = { node: node, buttonID: hit.button?.id };
          tempPointRef.current = mouse;
          return;
        }

        // Выходной порт ветки условия
        if (hit.type === "branch-out-point") {
          // toast.success(`MouseDown: Клик по ${hit.type} ${hit.branch?.id}`);
          linkingFromRef.current = { node: node, branchID: hit.branch?.id };
          tempPointRef.current = mouse;
          return;
        }

        // Выходной порт
        if (hit.type === "output") {
          // toast.success(`MouseDown: Клик по ${hit.type}`);
          linkingFromRef.current = { node: node };
          tempPointRef.current = mouse;
          return;
        }

        // Сам node
        if (inRect(mouse.x, mouse.y, node)) {
          activeNodeRef.current = node;
          isDraggingRef.current = true;

          offsetRef.current = {
            x: mouse.x - node.x,
            y: mouse.y - node.y,
          };
          return;
        }
      }

      /* =========================
        3️⃣ PAN (SCREEN)
      ========================== */
      // PAN только ЛКМ по пустому месту
      if (e.button === 0) {
        isPanning = true;
        startX = screenX;
        startY = screenY;
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      /**
       * Когда двигаем мышь:
       * 1. Режим протягивания стрелки. Это когда мы держим ЛКМ и двигаем мышью над стрелкой.
       * 2. Режим перетаскивания ноды. Это когда мы держим ЛКМ и двигаем мышью над нодой.
       * 3. Режим панорамирования.
       */
      e.preventDefault();

      const { mouse, screenX, screenY } = getMousePositions(e, canvas, canvasStateRef);

      mouseRef.current = mouse; // Store for draw()

      if (dragStartRef.current) {
        const dx = screenX - dragStartRef.current.x;
        const dy = screenY - dragStartRef.current.y;

        if (Math.hypot(dx, dy) > MOVE_THRESHOLD) {
          didMoveRef.current = true;
        }
      }

      /* =========================
        DRAG ARROW (WORLD)
      ========================== */
      if (linkingFromRef.current) {
        console.log("dragging arrow");
        tempPointRef.current = mouse;
        requestDraw();
        return;
      }

      // Nodes
      if (isDraggingRef.current && activeNodeRef.current) {
        console.log("dragging node");
        // смещение должно учитывать scale
        activeNodeRef.current.x = mouse.x - offsetRef.current.x;
        activeNodeRef.current.y = mouse.y - offsetRef.current.y;
        requestDraw();
        return;
      }

      /* =========================
        PAN (ТОЛЬКО SCREEN)
      ========================== */
      if (isPanning) {
        console.log("panning");
        canvasStateRef.current.offsetX += screenX - startX;
        canvasStateRef.current.offsetY += screenY - startY;

        startX = screenX;
        startY = screenY;

        requestDraw();
        return;
      }
    };

    const onMouseUp = (e: MouseEvent) => {
      console.log("onMouseUp");
      /**
       * Когда отпускаем кнопку мыши:
       * 1. Завершаем перетаскивание канваса
       * 2. Завершаем создание стрелки
       * 3. Завершаем панорамирование
       */
      const { mouse } = getMousePositions(e, canvas, canvasStateRef);

      try {
        // завершение создания стрелки
        if (linkingFromRef.current) {
          console.log("ending arrow");
          for (const toNode of nodesRef.current) {
            if (inRect(mouse.x, mouse.y, toNode)) {
              if (toNode.id === linkingFromRef.current.node.id) {
                throw new Error("Нельзя создать стрелку на ту же ноду, выберите другую ноду");
              }

              if (
                edgeExists(
                  edgesRef.current,
                  linkingFromRef.current.node.id,
                  linkingFromRef.current.buttonID ?? null,
                  toNode.id
                )
              ) {
                throw new Error("Стрелка уже существует");
              }

              let source: { type: "button" | "branch"; id: string } | undefined;

              if (linkingFromRef.current.buttonID !== undefined && linkingFromRef.current.buttonID !== null) {
                source = { type: "button", id: linkingFromRef.current.buttonID };
              } else if (linkingFromRef.current.branchID !== undefined && linkingFromRef.current.branchID !== null) {
                source = { type: "branch", id: linkingFromRef.current.branchID };
              }

              edgesRef.current.push({
                fromNodeID: linkingFromRef.current.node.id,
                source: source,
                toNodeID: toNode.id,
              });
              console.log("edgesRef.current", edgesRef.current);
              break;
            }
          }
        }
      } catch (error: any) {
        toast.error(error.message);
      }

      /* =========================
        RESET STATE
      ========================== */
      isPanning = false;
      linkingFromRef.current = null;
      tempPointRef.current = null;
      isDraggingRef.current = false;
      activeNodeRef.current = null;

      requestDraw();
    };

    const onClick = (e: MouseEvent) => {
      // Идея
      // 1. Слушаем canvas.click
      // 2. Получаем координаты клика
      // 3. Проверяем, попали ли в какой-то Node
      // 4. Вызываем нужное действие      
      if (didMoveRef.current) {
        // это был drag, а не клик
        console.log("это был drag, а не клик");
        return
      }

      const { mouse } = getMousePositions(e, canvas, canvasStateRef);

      // Находим элемент под курсором
      const node = nodesRef.current.find((n) => {
        return inRect(mouse.x, mouse.y, {
          ...n,
          x: n.x - NODE_PADDING_LEFT,
          y: n.y + NODE_MENU_OFFSET_Y
        });
      });
      if (node) {
        let payload: any = null;
        if (node.type === NodeType.MESSAGE) {
          const hit = hitTest(node, mouse.x, mouse.y);

          if (!hit) return;
          console.log("Клик по", hit.type);

          switch (hit.type) {
            case "delete-node": {
              console.log("Клик по кнопке-иконке удалить ноду");
              const confirmed = window.confirm("Вы уверены, что хотите удалить эту ноду?");
              if (confirmed) {
                nodesRef.current = nodesRef.current.filter((n) => n.id !== node.id);
                edgesRef.current = edgesRef.current.filter(
                  (e) => e.fromNodeID !== node.id && e.toNodeID !== node.id
                );
                requestDraw();
              }
              break;
            }
            case "node-stats": {
              console.log("Клик по stats");
              setStatsSidebarOpen(true);
              break;
            }
            case "title":
              console.log("Клик по title");
              break;
            case "text":
              console.log("Клик по тексту");
              setEditTextNodeOpen(true);
              break;
            case "button-delete": {
              console.log("Клик по кнопке удалить кнопку", hit.button?.text);
              node.buttons = node.buttons?.filter((b) => b.id !== hit.button?.id) ?? [];

              // Также удалить связанную стрелку
              edgesRef.current = edgesRef.current.filter((e) => e.source?.type !== "button" || e.source?.id !== hit.button?.id);
              break;
            }
            case "button-ctr": {
              console.log("Клик по CTR кнопки", hit.button?.text);
              payload = {
                button: hit.button,
                buttonIndex: hit.index,
              };
              setStatsSidebarOpen(true);
              break;
            }
            case "button":
              console.log("Клик по кнопке", hit.button?.text);
              payload = {
                button: hit.button,
                buttonIndex: hit.index,
              };
              setButtonSidebarOpen(true);
              break;
            case "add-button": {
              console.log("Клик по кнопке 'Добавить кнопку'");
              if (node.buttons && node.buttons.length < 3) {
                node.buttons.push({ id: Date.now().toString(), text: "", url: "" });
              } else {
                toast.error("Вы достигли максимального количества кнопок");
              }
              break;
            }
            case "node-body": {
              break;
            }
          }

          requestDraw();
        } else if (node.type === NodeType.ACTION) {
          const hit = hitTest(node, mouse.x, mouse.y);

          if (!hit) return;
          // toast.success(`Клик по ${hit.type}`);

          switch (hit.type) {
            case "delete-node": {
              console.log("Клик по кнопке-иконке удалить ноду");
              const confirmed = window.confirm("Вы уверены, что хотите удалить эту ноду?");
              if (confirmed) {
                nodesRef.current = nodesRef.current.filter((n) => n.id !== node.id);
                edgesRef.current = edgesRef.current.filter(
                  (e) => e.fromNodeID !== node.id && e.toNodeID !== node.id
                );
                requestDraw();
              }
              break;
            }
            case "title":
              console.log("Клик по title");
              break;
            case "text":
            case "node-body": {
              console.log("Клик по телу ноды");
              setActionSidebarOpen(true);
              break;
            }
          }
          requestDraw();
        } else if (node.type === NodeType.DELAY) {
          const hit = hitTest(node, mouse.x, mouse.y);

          if (!hit) return;
          // toast.success(`Клик по ${hit.type}`);

          switch (hit.type) {
            case "delete-node": {
              console.log("Клик по кнопке-иконке удалить ноду");
              const confirmed = window.confirm("Вы уверены, что хотите удалить эту ноду?");
              if (confirmed) {
                nodesRef.current = nodesRef.current.filter((n) => n.id !== node.id);
                edgesRef.current = edgesRef.current.filter(
                  (e) => e.fromNodeID !== node.id && e.toNodeID !== node.id
                );
                requestDraw();
              }
              break;
            }
            case "title":
              console.log("Клик по title");
              break;
            case "text":
            case "node-body": {
              console.log("Клик по телу ноды");
              setDelaySidebarOpen(true);
              break;
            }
          }
          requestDraw();
        } else if (node.type === NodeType.CONDITION) {
          const hit = hitTest(node, mouse.x, mouse.y);
          if (!hit) return;
          // toast.success(`Клик по ${hit.type}`);
          switch (hit.type) {
            case "delete-node": {
              console.log("Клик по кнопке-иконке удалить ноду");
              const confirmed = window.confirm("Вы уверены, что хотите удалить эту ноду?");
              if (confirmed) {
                nodesRef.current = nodesRef.current.filter((n) => n.id !== node.id);
                edgesRef.current = edgesRef.current.filter(
                  (e) => e.fromNodeID !== node.id && e.toNodeID !== node.id
                );
                requestDraw();
              }
              break;
            }
            case "title":
              console.log("Клик по title");
              break;
            case "branch": {
              console.log("Клик по ветке", hit.index);
              payload = {
                branchIndex: hit.index,
              };
              setBranchSidebarOpen(true);
              break;
            }
            case "branch-delete": {
              console.log("Клик по кнопке удалить ветку", hit.index);
              if (node.data?.payload?.branches) {
                node.data.payload.branches = node.data.payload.branches.filter((b: any, index: number) => index !== hit.index);
              }
              break;
            }
            case "branch-out-point": {
              console.log("Клик по выходному порту ветки", hit.index);
              break;
            }
            case "add-branch": {
              console.log("Клик по кнопке 'Добавить ветку'");
              const prevNodeData = node.data
              if (!prevNodeData) {
                node.data = {
                  type: "condition",
                  payload: {
                    branches: [],
                  },
                };
              }

              if (prevNodeData?.payload?.branches) {
                const newBranch = { id: Date.now(), operator: "AND", conditions: [] }
                node.data = {
                  type: "condition",
                  payload: {
                    branches: [...prevNodeData.payload.branches, newBranch],
                  },
                }
              }
              break;
            }


          }
          requestDraw();
        }

        activeNodeRef.current = node;
        setActiveTarget({
          type: "node",
          entityID: node.id,
          entity: node,
          payload: payload,
        });
      }

      // Стрелки
      const hit = hitTestEdges(edgesRef.current, mouse.x, mouse.y);
      if (hit) {
        // toast.success("Стрелка выбрана");
        // удаляем стрелку
        edgesRef.current = edgesRef.current.filter((e, index) => index !== hit.index);
        requestDraw();
        return;
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.key === "Delete" || e.key === "Backspace") && selectedEdgeRef.current) {
        edgesRef.current = edgesRef.current.filter((e) => e !== selectedEdgeRef.current);
        selectedEdgeRef.current = null;
        requestDraw();
      }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      // Determine the direction of the zoom
      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;

      // 1. Get world position under cursor before zoom
      const mouseBeforeZoom = screenToWorld(screenX, screenY, canvasStateRef);

      // 2. Calculate new scale
      const { scaleStep } = canvasStateRef.current;
      const zoomFactor = e.deltaY < 0 ? scaleStep : 1 / scaleStep;

      const newScale = canvasStateRef.current.scale * zoomFactor;

      // Optional: Limit zoom range (e.g., 0.1 to 3.0)
      if (newScale >= 0.5 && newScale < 2) {
        canvasStateRef.current.scale = newScale;

        // 3. Adjust offset so mouseBeforeZoom is still at screenX/screenY
        // Formula: Offset = ScreenPos - (WorldPos * NewScale)
        canvasStateRef.current.offsetX = screenX - mouseBeforeZoom.x * newScale;
        canvasStateRef.current.offsetY = screenY - mouseBeforeZoom.y * newScale;

        requestDraw();
      }
    };

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("click", onClick);
    canvas.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("mouseleave", onMouseUp);
    window.addEventListener("keydown", onKeyDown);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    requestDraw();

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("click", onClick);
      canvas.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("keydown", onKeyDown);
      canvas.removeEventListener("wheel", onWheel);
    };
  }, []);

  // ⬅️ перерисовывается ВСЁ
  const draw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const { scale, offsetX, offsetY } = canvasStateRef.current;

    // Сбрасываем трансформацию, но сохраняем DPR масштабирование!
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

    // Применяем вашу камеру (Зум и Панорамирование)
    ctx.translate(offsetX, offsetY);
    ctx.scale(scale, scale);

    /**
       * Canvas — это «перерисовать», а не «двинуть»
        Ты всегда:      
        1. меняешь данные (x / y)
        2. очищаешь canvas
        3.рисуешь всё заново
      */
    // 2. Ноды
    if (nodesRef.current && nodesRef.current.length > 0) {
      nodesRef.current.forEach((node) => drawNode(ctx, node));
    }

    // 1. Рисуем постоянные стрелки    
    if (edgesRef.current && edgesRef.current.length > 0) {
      edgesRef.current.forEach((edge) => {
        const layout: EdgeLayout = {};

        const fromNode = nodesRef.current.find((n) => n.id === edge.fromNodeID);
        const toNode = nodesRef.current.find((n) => n.id === edge.toNodeID);
        if (!fromNode || !toNode) return;

        const isSelected = selectedEdgeRef.current === edge;
        ctx.strokeStyle = isSelected ? "red" : "red";
        ctx.lineWidth = isSelected ? 30 : 20;

        let fromPoint;

        if (
          fromNode.type === NodeType.MESSAGE &&
          edge.source?.type === "button"
        ) {
          // Стрелка из кнопки
          const fromNodeLayouot = nodesRef.current.find((n) => n.id === edge.fromNodeID)?.layout
          if (!fromNodeLayouot) return;

          const buttonID = edge.source?.id;
          const buttonIndex = fromNode.buttons?.findIndex((b) => b.id === buttonID);

          fromPoint = fromNodeLayouot?.buttonOutPoint?.[buttonIndex!];
        } else if (fromNode.type === NodeType.CONDITION && edge.source?.type === "branch") {
          // Стрелка из ветки условия
          const fromNodeLayouot = nodesRef.current.find((n) => n.id === edge.fromNodeID)?.layout
          if (!fromNodeLayouot) return;

          const branchID = edge.source?.id;
          const branchIndex = fromNode.data?.payload?.branches?.findIndex((b: any) => b.id === branchID);

          fromPoint = fromNodeLayouot?.branchOutPoint?.[branchIndex!];
        } else {
          // Точка выхода из ноды
          fromPoint = getOutPoint(fromNode);
        }

        if (!fromPoint) return;
        drawCubicArrow(ctx, fromPoint, getInPoint(toNode), (pos) => {
          const size = 16;
          layout.trashRect = {
            x: pos.x - size / 2,
            y: pos.y - size / 2,
            w: size,
            h: size,
          };
        });

        edge.layout = layout;
      });
    }

    // 3. Рисуем временную стрелку
    if (linkingFromRef.current && tempPointRef.current) {
      let outPoint: { x: number; y: number } | undefined;
      const nodeLayout = nodesRef.current.find(
        (n) => n.id === linkingFromRef.current?.node.id
      )?.layout;
      if (!nodeLayout) return;

      if (linkingFromRef.current?.buttonID) {
        // Button output point
        const buttonIndex = linkingFromRef.current?.node.buttons?.findIndex((b) => b.id === linkingFromRef.current?.buttonID);
        outPoint = nodeLayout?.buttonOutPoint![buttonIndex!];
      } else if (linkingFromRef.current?.branchID) {
        // Branch output point
        const branchIndex = linkingFromRef.current?.node.data?.payload?.branches?.findIndex((b: any) => b.id === linkingFromRef.current?.branchID);
        outPoint = nodeLayout?.branchOutPoint![branchIndex!];
      } else {
        // Node output point
        outPoint = nodeLayout?.outPoint;
      }

      if (!outPoint) return;
      drawCubicArrow(ctx, outPoint, tempPointRef.current);
    }
  };

  /**
   * во время draw() сохранять рассчитанные прямоугольники элементов в node, а в hit-test просто брать их, а не пересчитывать заново.
   * @param ctx
   * @param node
   */
  const drawNode = (ctx: CanvasRenderingContext2D, node: Node) => {
    const layout: NodeLayout = {
      buttonRects: [],
      buttonCTRRect: [],
      buttonOutPoint: [],
      buttonDeletes: [],

      branchRects: [],
      branchOutPoint: [],
      branchDeletes: [],
    };

    // Кнопка-иконка удалить ноду
    ctx.beginPath();
    // смещение иконки удалить ноду вверх на 15px относительно ноды за рамки node
    const offsetY = -15;
    ctx.drawImage(deleteNodeIconRef.current, node.x + 10, node.y + offsetY, 10, 10);

    layout.deleteNodeIcon = {
      x: node.x + 10,
      y: node.y + offsetY,
      w: 10,
      h: 10,
    };

    // Вычисляем динамическую высоту title и text
    ctx.font = "14px Inter, Arial";
    const titleHeight = measureTextHeight(ctx, "Сообщение", node.w - 20, 14);

    ctx.font = "12px Inter, Arial";
    const textHeight = measureTextHeight(ctx, node.text, node.w - 20, 16);

    // title
    let titleY = node.y + NODE_PADDING_TOP;
    layout.titleRect = {
      x: node.x,
      y: titleY,
      w: node.w - 30,
      h: titleHeight,
    };

    // text
    const TEXT_TOP_PADDING = 16;
    let textY = titleY + titleHeight + TEXT_TOP_PADDING;


    // Вычисляем start Y для кнопок
    const buttonsStartY = textY + textHeight + BUTTON_PADDING_TOP;
    const branchStartY = textY + textHeight + BUTTON_PADDING_TOP;

    if (node.type == NodeType.MESSAGE) {
      layout.textRect = {
        x: node.x,
        y: textY,
        w: node.w,
        h: textHeight,
      };

      // 🔥 пересчёт высоты
      node.h =
        NODE_PADDING_TOP +
        titleHeight +
        TEXT_TOP_PADDING +
        textHeight +
        BUTTON_PADDING_TOP +
        (node.buttons?.length ?? 0) * (BUTTON_HEIGHT + BUTTON_ROW_GAP) +
        BUTTON_HEIGHT +
        OUTPUT_HEIGHT +
        BUTTON_PADDING_TOP +
        NODE_PADDING_BOTTOM;

      // Сам node
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.roundRect(node.x, node.y, node.w, node.h, 10);
      ctx.strokeStyle = "#cbd5e1";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fill();


      // Instagram logo
      ctx.drawImage(messageIconRef.current, node.x + 10, node.y + 10, 10, 10);

      // Node type
      ctx.fillStyle = "#000";
      ctx.font = "14px Inter, Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      wrapText(ctx, "Сообщение", node.x + node.w / 2, titleY, node.w - 30, titleHeight);


      // Stats: Delivered count
      ctx.fillStyle = "#000";
      ctx.font = "10px Inter, Arial";
      wrapText(ctx, node.stats?.delivered?.toString() ?? "0",
        node.x + node.w - NODE_PADDING_LEFT,
        titleY,
        30,
        12
      );
      layout.statsRect = {
        x: node.x + node.w - 30,
        y: titleY,
        w: 30,
        h: 16,
      };

      // Текст "Сообщение"
      ctx.fillStyle = "#333";
      ctx.font = "12px Inter, Arial";
      wrapText(ctx, node.text || "Введите сообщение", node.x + node.w / 2, textY, node.w - 20, 16);

      // Buttons
      if (node.buttons && node.buttons.length > 0) {
        node.buttons.forEach((button: any, index: number) => {
          // index — порядковый номер кнопки (0,1,2...)
          const buttonRect = drawButton(ctx, node, button, index, buttonsStartY, layout);

          layout.buttonRects!.push(buttonRect);

          // output порт кнопки
          const circleX = buttonRect.x + buttonRect.w - 10;
          const circleY = buttonRect.y + buttonRect.h / 2;
          drawCircle(ctx, circleX, circleY, 5, "#64748b");

          layout.buttonOutPoint!.push({
            x: buttonRect.x + buttonRect.w - 10,
            y: circleY - 5,
            w: 10,
            h: 10,
          });

          // Удалить кнопку
          const deleteBtnX = buttonRect.x - 15;
          const deleteBtnY = buttonRect.y + buttonRect.h / 2 - 5;
          ctx.drawImage(deleteNodeIconRef.current, deleteBtnX, deleteBtnY, 10, 10);
          layout.buttonDeletes!.push({
            x: deleteBtnX,
            y: deleteBtnY,
            w: 10,
            h: 10,
          });
        });
      }

      // 🔹 Рисуем отдельную кнопку "Добавить кнопку" в конце
      const addBtnY =
        buttonsStartY + (node.buttons?.length ?? 0) * (BUTTON_HEIGHT + BUTTON_ROW_GAP);
      ctx.fillStyle = "#f8fafc";
      ctx.fillRect(node.x + 10, addBtnY, node.w - 20, BUTTON_HEIGHT);
      ctx.strokeStyle = "#cbd5e1";
      ctx.strokeRect(node.x + 10, addBtnY, node.w - 20, BUTTON_HEIGHT);
      ctx.fillStyle = "#0f172a";
      ctx.font = "10px Inter, Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      wrapText(ctx, "+ Добавить кнопку", node.x + node.w / 2, addBtnY + 6, node.w - 20, 12);

      layout.addButtonRect = {
        x: node.x + 10,
        y: addBtnY,
        w: node.w - 20,
        h: BUTTON_HEIGHT,
      };
    }

    if (node.type == NodeType.ACTION) {
      layout.textRect = {
        x: node.x,
        y: textY,
        w: node.w,
        h: textHeight,
      };

      const actionType = node.data?.type ?? "";
      const actionPayload = node.data?.payload ?? {};
      const actionTypeSchema = actionTypesMap[actionType] ?? { schema: { fields: [] } };
      const fieldsCount = actionTypeSchema.schema.fields.length;

      const ACTION_ITEM_HEIGHT = 12;
      const ACTION_ITEM_GAP = 4;
      const ACTION_BODY_TOP_PADDING = 16;
      const ACTION_BODY_BOTTOM_PADDING = 16;
      const ACTION_BODY_PADDING = 4;
      const ACTION_BODY_HEIGHT = ACTION_BODY_TOP_PADDING + ACTION_BODY_BOTTOM_PADDING + fieldsCount * (ACTION_ITEM_HEIGHT + ACTION_ITEM_GAP);

      // 🔥 пересчёт высоты
      node.h =
        NODE_PADDING_TOP +
        titleHeight +
        ACTION_BODY_TOP_PADDING +
        ACTION_BODY_HEIGHT +
        ACTION_BODY_BOTTOM_PADDING +
        OUTPUT_HEIGHT +
        NODE_PADDING_BOTTOM;

      // Сам node
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.roundRect(node.x, node.y, node.w, node.h, 10);
      ctx.strokeStyle = "#cbd5e1";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fill();

      // Action node
      ctx.drawImage(actionIconRef.current, node.x + 10, node.y + 10, 10, 10);

      // Action text
      ctx.fillStyle = "#000";
      ctx.font = "14px Inter, Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      wrapText(ctx, "Действие", node.x + node.w / 2, titleY, node.w - 20, 14);


      // Action type
      if (actionType && actionPayload) {
        const actionBodyY = titleY + titleHeight + ACTION_BODY_TOP_PADDING;

        // Draw action body
        ctx.fillStyle = "#f8fafc";
        ctx.fillRect(node.x + 10, actionBodyY, node.w - 20, ACTION_BODY_HEIGHT);
        ctx.strokeStyle = "#cbd5e1";
        ctx.strokeRect(node.x + 10, actionBodyY, node.w - 20, ACTION_BODY_HEIGHT);

        layout.bodyRect = {
          x: node.x + 10,
          y: actionBodyY,
          w: node.w - 20,
          h: ACTION_BODY_HEIGHT,
        };

        let actionItemY = actionBodyY + ACTION_ITEM_GAP;

        // текст        
        ctx.fillStyle = "#64748b";
        ctx.font = "10px Inter, Arial";
        ctx.textBaseline = "top";
        ctx.textAlign = "left";
        wrapText(ctx, actionTypeSchema.label || "", node.x + NODE_PADDING_LEFT + ACTION_BODY_PADDING, actionItemY, node.w - 20, 12);


        // Payload
        if (actionPayload) {
          const fields = actionTypeSchema?.schema?.fields ?? [];
          if (fields) {
            fields.forEach((field: any) => {
              if (_.get(actionPayload, field.key)) {
                const fieldValue = _.get(actionPayload, field.key);

                ctx.fillStyle = "#000";

                if (field.type === "string" && fieldValue) {
                  wrapText(ctx, fieldValue, node.x + NODE_PADDING_LEFT + ACTION_BODY_PADDING, actionItemY + ACTION_ITEM_HEIGHT, node.w - 20, 12);
                }
                if (field.type === "string[]" && fieldValue) {
                  wrapText(ctx, fieldValue.join(","), node.x + NODE_PADDING_LEFT + ACTION_BODY_PADDING, actionItemY + ACTION_ITEM_HEIGHT, node.w - 20, 12);
                }
              }

              if (layout.bodyRect) {
                actionItemY += ACTION_ITEM_HEIGHT + ACTION_ITEM_GAP;
              }
            });
          }
        }
      }

      node.layout = layout;
    }
    if (node.type == NodeType.DELAY) {
      layout.textRect = {
        x: node.x,
        y: textY,
        w: node.w,
        h: textHeight,
      };

      const delayPayload = node.data?.payload ?? {};
      const delayValue = delayPayload?.delay ?? 0;

      const ACTION_ITEM_HEIGHT = 12;
      const ACTION_ITEM_GAP = 4;
      const ACTION_BODY_TOP_PADDING = 16;
      const ACTION_BODY_BOTTOM_PADDING = 16;
      const ACTION_BODY_PADDING = 4;
      const ACTION_BODY_HEIGHT = ACTION_BODY_TOP_PADDING + ACTION_BODY_BOTTOM_PADDING;

      // 🔥 пересчёт высоты
      node.h =
        NODE_PADDING_TOP +
        titleHeight +
        ACTION_BODY_TOP_PADDING +
        ACTION_BODY_HEIGHT +
        ACTION_BODY_BOTTOM_PADDING +
        OUTPUT_HEIGHT +
        NODE_PADDING_BOTTOM;

      // Сам node
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.roundRect(node.x, node.y, node.w, node.h, 10);
      ctx.strokeStyle = "#cbd5e1";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fill();

      // Delay node
      ctx.drawImage(delayIconRef.current, node.x + 10, node.y + 10, 10, 10);

      // Delay text
      ctx.fillStyle = "#000";
      ctx.font = "14px Inter, Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      wrapText(ctx, "Задержка", node.x + node.w / 2, titleY, node.w - 20, 14);


      // Delay type
      if (delayValue) {
        const delayBodyY = titleY + titleHeight + ACTION_BODY_TOP_PADDING;

        // Draw action body
        ctx.fillStyle = "#f8fafc";
        ctx.fillRect(node.x + 10, delayBodyY, node.w - 20, ACTION_BODY_HEIGHT);
        ctx.strokeStyle = "#cbd5e1";
        ctx.strokeRect(node.x + 10, delayBodyY, node.w - 20, ACTION_BODY_HEIGHT);

        layout.bodyRect = {
          x: node.x + 10,
          y: delayBodyY,
          w: node.w - 20,
          h: ACTION_BODY_HEIGHT,
        };

        let delayItemY = delayBodyY + ACTION_ITEM_GAP;

        // текст        
        ctx.fillStyle = "#64748b";
        ctx.font = "10px Inter, Arial";
        ctx.textBaseline = "top";
        ctx.textAlign = "left";
        wrapText(ctx, "Задержка", node.x + NODE_PADDING_LEFT + ACTION_BODY_PADDING, delayItemY, node.w - 20, 12);


        // Payload
        if (delayValue) {
          wrapText(ctx, delayValue.toString(), node.x + NODE_PADDING_LEFT + ACTION_BODY_PADDING, delayItemY + ACTION_ITEM_HEIGHT, node.w - 20, 12);
        }
      }

      node.layout = layout;
    }

    if (node.type == NodeType.CONDITION) {
      // 🔥 пересчёт высоты
      node.h =
        NODE_PADDING_TOP +
        titleHeight +
        TEXT_TOP_PADDING +
        BUTTON_PADDING_TOP +
        (node.data?.payload?.branches.length ?? 0) * (BUTTON_HEIGHT + BUTTON_ROW_GAP) +
        BUTTON_HEIGHT +
        OUTPUT_HEIGHT +
        BUTTON_PADDING_TOP +
        NODE_PADDING_BOTTOM;

      // Сам node
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.roundRect(node.x, node.y, node.w, node.h, 10);
      ctx.strokeStyle = "#cbd5e1";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fill();

      // Condition logo
      ctx.drawImage(conditionIconRef.current, node.x + 10, node.y + 10, 10, 10);

      // Condition tit
      ctx.fillStyle = "#000";
      ctx.font = "14px Inter, Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      wrapText(ctx, "Условие", node.x + node.w / 2, titleY, node.w - 20, 14);

      // NodeData.branches
      const payload = node.data?.payload ?? {};
      if (payload?.branches && payload?.branches.length > 0) {
        payload.branches.forEach((branch: any, index: number) => {
          // index — порядковый номер кнопки (0,1,2...)
          const branchRect = drawConditionBranch(ctx, node, branch, index, branchStartY);

          layout.branchRects!.push(branchRect);

          // output порт кнопки
          const circleX = branchRect.x + branchRect.w - 10;
          const circleY = branchRect.y + branchRect.h / 2;
          drawCircle(ctx, circleX, circleY, 5, "#64748b");

          layout.branchOutPoint!.push({
            x: branchRect.x + branchRect.w - 10,
            y: circleY - 5,
            w: 10,
            h: 10,
          });

          // Удалить кнопку
          const deleteBtnX = branchRect.x - 15;
          const deleteBtnY = branchRect.y + branchRect.h / 2 - 5;
          ctx.drawImage(deleteNodeIconRef.current, deleteBtnX, deleteBtnY, 10, 10);
          layout.branchDeletes!.push({
            x: deleteBtnX,
            y: deleteBtnY,
            w: 10,
            h: 10,
          });
        });
      }

      // 🔹 Рисуем отдельную кнопку "Добавить условие" в конце
      const addBtnY =
        branchStartY + (payload.branches?.length ?? 0) * (BUTTON_HEIGHT + BUTTON_ROW_GAP);
      ctx.fillStyle = "#f8fafc";
      ctx.fillRect(node.x + 10, addBtnY, node.w - 20, BUTTON_HEIGHT);
      ctx.strokeStyle = "#cbd5e1";
      ctx.strokeRect(node.x + 10, addBtnY, node.w - 20, BUTTON_HEIGHT);
      ctx.fillStyle = "#0f172a";
      ctx.font = "10px Inter, Arial";
      wrapText(ctx, "+ Добавить условие", node.x + node.w / 2, addBtnY + 6, node.w - 20, 12);

      layout.addBranchRect = {
        x: node.x + 10,
        y: addBtnY,
        w: node.w - 20,
        h: BUTTON_HEIGHT,
      };
    }

    // Выходной порт
    const outPoint = getOutPoint(node);
    if (outPoint) {
      drawCircle(ctx, outPoint.x, outPoint.y, outPoint.w, "#64748b");
      layout.outPoint = outPoint;
    }

    // ✅ сохраняем layout в node
    node.layout = layout;
  };

  // Оптимизация перерисовки
  const requestDraw = () => {
    if (!drawRef.current) {
      drawRef.current = true;
      requestAnimationFrame(() => {
        drawRef.current = false;
        draw();
      });
    }
  };

  const addNode = (type: NodeType) => {
    const canvas = canvasRef.current;
    const w = 200;
    const h = 100;

    // Центр видимой области канваса в мировых координатах
    let x = 40 + nodesRef.current.length * 20;
    let y = 40 + nodesRef.current.length * 20;

    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const center = screenToWorld(rect.width / 2, rect.height / 2, canvasStateRef);
      // небольшое смещение, чтобы ноды не накладывались полностью друг на друга
      const stagger = (nodesRef.current.length % 5) * 20;
      x = center.x - w / 2 + stagger;
      y = center.y - h / 2 + stagger;
    }

    nodesRef.current.push({
      id: Date.now().toString(),
      x,
      y,
      w,
      h,
      type: type,
      text: "",
      image: null,
      buttons: [],
    });

    requestDraw();
    setMenuOpen(null);
  };

  const onSubmitFlow = async (formData: any) => {
    try {
      await automationsService.updateAutomation(automationID, {
        nodes: nodesRef.current,
        edges: edgesRef.current,
        canvasState: canvasStateRef.current,
      });
      toast.success("Автоматизация сохранена успешно", {
        position: "bottom-right",
      });
    } catch (error) {
      toast.error("Ошибка при сохранении автоматизации");
    }
  };

  const onCloseTriggerSidebar = async () => {
    setTriggerSidebarOpen(false);

    try {
      if (
        !methods.getValues("automationTriggers") ||
        methods.getValues("automationTriggers").length === 0
      ) {
        return;
      }
      await automationsService.updateAutomation(Number(automationID), {
        automationTriggers: methods.getValues("automationTriggers"),
      });
      // toast.success("Автоматизация сохранена успешно");
    } catch (error) {
      console.error("Error updating automation", error);
      toast.error("Ошибка при сохранении автоматизации");
    }
  };

  const onCloseEditTextNode = () => {
    setEditTextNodeOpen(false);
  };

  const onReset = () => {
    nodesRef.current = [];
    edgesRef.current = [];
    requestDraw();
  };

  const onDebug = () => {
    console.log("nodesRef", nodesRef.current);
    console.log("edgesRef", edgesRef.current);
    console.log("activeTarget", activeTarget);
    console.log("activeNodeRef", activeNodeRef.current);
    console.log("linkingFromRef", linkingFromRef.current);
    console.log("tempPointRef", tempPointRef.current);
  };

  const onCloseButtonSidebar = () => {
    setButtonSidebarOpen(false);
  };

  /**
   * Обновляем кнопку на ноде
   * @param button - новая кнопка
   */
  const updateNodeButton = (button: ButtonData) => {
    if (activeTarget?.type === "node") {
      nodesRef.current = nodesRef.current.map((node) =>
        node.id === activeTarget?.entityID
          ? {
            ...(activeTarget?.entity as Node),
            buttons: node.buttons?.map((b) => (b.id === button.id ? button : b)) ?? [],
          }
          : node
      );
    }
    requestDraw();
    setButtonSidebarOpen(false);
  };

  const onCloseActionSidebar = () => {
    setActionSidebarOpen(false);
  };

  /**
   * Обновляем ноду на канвасе
   * @param newNode - новая нода
   */
  const updateNode = (newNode: Node) => {
    if (activeTarget?.type === "node") {
      nodesRef.current = nodesRef.current.map((node) => {
        if (node.id === activeTarget?.entityID) {
          return {
            ...node,
            ...newNode,
          };
        }
        return node;
      });
      requestDraw();
      setActionSidebarOpen(false);
      setDelaySidebarOpen(false);
    }
  };

  const onCloseDelaySidebar = () => {
    setDelaySidebarOpen(false);
  };
  const onCloseBranchSidebar = () => {
    setBranchSidebarOpen(false);
  };

  const updateNodeBranch = (updatedBranch: any) => {
    console.log("onSaveBranch", updatedBranch);

    nodesRef.current = nodesRef.current.map((node) => {
      if (node.id === activeTarget?.entityID) {
        return {
          ...node,
          data: {
            type: "condition",
            payload: {
              branches: node.data?.payload?.branches?.map((b: any) => b.id === updatedBranch.id ? updatedBranch : b) ?? [],
            }
          },
        };
      }
      return node;
    });

    requestDraw();
    setBranchSidebarOpen(false);
  };

  const onCloseStatsSidebar = () => {
    setStatsSidebarOpen(false);
  };

  return (
    <div className="flow-container">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmitFlow)}>
          <div className="flow-header relative flex items-center justify-between w-100">
            <div className="flow-title text-lg flex items-center justify-center gap-2">
              <FaArrowLeft className="cursor-pointer" onClick={() => {
                document.getElementById("save-flow-button")?.click();
                navigate("/admin/automations")
              }} />
              <span>{automation.name}</span>
            </div>
            <div className="flow-header-buttons gap-2 flex items-center">
              <Button variant="outlined" onClick={() => setTriggerSidebarOpen(true)}>
                Триггер запуска
              </Button>
              <Button variant="outlined" onClick={onSubmitFlow} id="save-flow-button">
                <FaSave size={20} />
              </Button>
              {appContext.isDev && (
                <Button variant="outlined" onClick={onDebug}>
                  Debug
                </Button>
              )}
            </div>
          </div>
          <div className="flow-buttons">
            <IconButton onClick={(e) => setMenuOpen(e.currentTarget)}>
              <FaPlus />
            </IconButton>
            <Menu anchorEl={menuOpen} open={Boolean(menuOpen)} onClose={() => setMenuOpen(null)}>
              <MenuItem onClick={() => addNode(NodeType.MESSAGE)}>
                {messageIconRef.current.src && <img src={messageIconRef.current.src} alt="Message" className="w-4 h-4 mr-2" />}
                Сообщение
              </MenuItem>
              <MenuItem onClick={() => addNode(NodeType.ACTION)}>
                {actionIconRef.current.src && <img src={actionIconRef.current.src} alt="Action" className="w-4 h-4 mr-2" />}
                Действие
              </MenuItem>
              <MenuItem onClick={() => addNode(NodeType.DELAY)}>
                {delayIconRef.current.src && <img src={delayIconRef.current.src} alt="Delay" className="w-4 h-4 mr-2" />}
                Задержка
              </MenuItem>
              <MenuItem onClick={() => addNode(NodeType.CONDITION)}>
                {conditionIconRef.current.src && <img src={conditionIconRef.current.src} alt="Condition" className="w-4 h-4 mr-2" />}
                Условие
              </MenuItem>
            </Menu>
          </div>
          <div className="zoom-buttons">
            <Button variant="outlined" onClick={() => {
              canvasStateRef.current.scale += 0.1;
              requestDraw();
            }}>
              <FaPlus />
            </Button>
            <Button variant="outlined" onClick={() => {
              canvasStateRef.current.scale -= 0.1;
              requestDraw();
            }}>
              <FaMinus />
            </Button>
          </div>
          <div className="flow-content">
            <canvas ref={canvasRef} style={{ width: "100%", height: "100vh" }} />
          </div>
          {/* Trigger sidebar */}
          <Drawer open={triggerSidebarOpen} anchor="right" onClose={onCloseTriggerSidebar}>
            <div style={{ minWidth: "400px" }}>
              <div className="drawer-header p-4 flex items-center justify-between">
                <div className="drawer-title text-lg font-bold">Триггер запуска</div>
                <Button variant="outlined" onClick={onCloseTriggerSidebar}>
                  <FaTimes />
                </Button>
              </div>
              <div className="drawer-content p-4">
                <div className="flex items-center gap-2 mb-4">
                  <AutomationTriggerV2 triggerTypes={triggerTypes} />
                </div>
              </div>
            </div>
          </Drawer>

          {/* Button sidebar */}
          <Drawer open={buttonSidebarOpen} anchor="right" onClose={onCloseButtonSidebar}>
            <div style={{ minWidth: "400px" }}>
              <div className="drawer-header p-4 flex items-center justify-between">
                <div className="drawer-title text-lg font-bold">Редактирование кнопки</div>
                <Button variant="outlined" onClick={() => setButtonSidebarOpen(false)}>
                  <FaTimes />
                </Button>
              </div>
              <div className="drawer-content p-4">
                <ButtonEditForm target={activeTarget ?? null} onSave={updateNodeButton} />
              </div>
            </div>
          </Drawer>

          {/* Action sidebar */}
          <Drawer open={actionSidebarOpen} anchor="right" onClose={onCloseActionSidebar}>
            <div style={{ minWidth: "400px" }}>
              <div className="drawer-header p-4 flex items-center justify-between">
                <div className="drawer-title text-lg font-bold">Редактирование действия</div>
              </div>
            </div>
            <div className="drawer-content p-4">
              <ActionEditForm target={activeTarget ?? null} onSave={updateNode} />
            </div>
          </Drawer>

          {/* Delay sidebar */}
          <Drawer open={delaySidebarOpen} anchor="right" onClose={onCloseDelaySidebar}>
            <div style={{ minWidth: "400px" }}>
              <div className="drawer-header p-4 flex items-center justify-between">
                <div className="drawer-title text-lg font-bold">Редактирование задержки</div>
              </div>
              <div className="drawer-content p-4">
                <DelayEditForm target={activeTarget ?? null} onSave={updateNode} />
              </div>
            </div>
          </Drawer>

          {/* Condition sidebar */}
          <Drawer open={branchSidebarOpen} anchor="right" onClose={onCloseBranchSidebar}>
            <div style={{ minWidth: "400px" }}>
              <div className="drawer-header p-4 flex items-center justify-between">
                <div className="drawer-title text-lg font-bold">Редактирование условия</div>
              </div>
            </div>
            <div className="drawer-content p-4">
              <ConditionEditForm target={activeTarget ?? null} onSave={updateNodeBranch} />
            </div>
          </Drawer>
        </form>
      </FormProvider>
      {/* Edit text of Node */}
      <Dialog open={editTextNodeOpen} onClose={onCloseEditTextNode}>
        <DialogTitle>Редактирование текста</DialogTitle>
        <DialogContent sx={{ minWidth: "400px" }}>
          <NodeTextForm
            node={activeTarget?.entity as Node}
            onSave={(text: string) => {
              console.log("onSave", {
                text,
                entity: activeTarget?.entity,
              });

              if (activeTarget?.type === "node") {
                nodesRef.current = nodesRef.current.map((node) =>
                  node.id === activeTarget?.entityID
                    ? {
                      ...(activeTarget?.entity as Node),
                      text: text,
                    }
                    : node
                );
                requestDraw();
                setEditTextNodeOpen(false);
              }
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={statsSidebarOpen} onClose={onCloseStatsSidebar}>
        {activeTarget && (
          <DialogContent sx={{ minWidth: "400px" }}>
            {activeTarget?.payload?.button ?
              <ButtonStatsDialog target={activeTarget} automationID={automation?.id} /> :
              <NodeStatsDialog target={activeTarget} automationID={automation?.id} />
            }
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default withRouter(
  withRemoteDataAndSpinner(AutomationConstructor, (router: any) => {
    let requests = {
      automation: () => automationsService.getAutomation(router.params.automationID),
      triggerTypes: () => triggerTypesAdminService.getAll(),
    };
    return useLoadAll(requests);
  })
);
